import React from 'react';
import { Title, Loader, ReloadIconButton, ActionTypeSelect, ClearFilters } from '../../elements';
import {
  Button,
  Chip,
  Collapse,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_BUTTON_DEFAULT, V4_CHIP_DEFAULT, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JSONTree } from 'react-json-tree';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { UserService } from '../../hooks';
import { notifications } from '../../utils';
import { UserActionLog, UserActionLogType } from '../../models/user';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { UserActionTypeDataMap } from '../../elements/ActionTypeSelect';
import { colors } from '../../utils';

interface IProps {
  userId: string;
}

export interface UserActionLogViewHandle {
  reload: () => void;
}

const UserActionLogView = React.forwardRef<UserActionLogViewHandle, IProps>(({ userId }, ref) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const userService = new UserService();

  const [actionLogs, setActionLogs] = React.useState([] as UserActionLog[]);

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const [actionType, setActionType] = React.useState(0 as UserActionLogType);
  const [number, setNumber] = React.useState('');
  const [macAddress, setMacAddress] = React.useState('');

  const handleChipNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value);
  };

  const handleMacAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMacAddress(event.target.value);
  };

  const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null>(new Date());
  const [selectedDateTo, setSelectedDateTo] = React.useState<Date | null>(new Date());

  const handleDateChangeFrom = (value: unknown) => {
    setSelectedDateFrom(value as Date | null);
  };

  const handleDateChangeTo = (value: unknown) => {
    setSelectedDateTo(value as Date | null);
  };

  const setToday = () => {
    const today = new Date();
    setSelectedDateFrom(today);
    setSelectedDateTo(today);
  };

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  React.useEffect(() => {
    setActionLogs([] as UserActionLog[]);
    setTotal(0);
    setPage(0);

    setLoaded(false);
  }, [userId]);

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadUserLogs();
      loadData();
    }
  }, [loaded]);

  //external reload call
  React.useImperativeHandle(ref, () => ({
    reload() {
      setLoaded(false);
    }
  }));

  const loadUserLogs = async () => {
    if (!selectedDateFrom || !selectedDateTo) {
      notifications.error(t('f-date-range-err'));
      return;
    }

    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    userService
      .searchUserActionLog(
        userId,
        actionType,
        number,
        macAddress,
        selectedDateFrom,
        selectedDateTo,
        searchPage,
        rowsPerPage
      )
      .then((result) => {
        setActionLogs(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(e?.error?.message ?? e.toString());
        setLoaded(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const renderActionTypeColumn = (type: UserActionLogType) => {
    return (
      <Grid container direction="row" alignItems="center">
        <FontAwesomeIcon size="1x" icon={UserActionTypeDataMap[type]?.icon} />
        &nbsp;
        {t(UserActionTypeDataMap[type]?.trans)}
      </Grid>
    );
  };

  function ActionLogTableRow(props: { log: UserActionLog }) {
    const { log } = props;
    const [rowExpanded, setRowExpanded] = React.useState(false);
    const logTime = new Date(log.dateTime);

    return (
      <React.Fragment key={log.id}>
        <TableRow key={`btn${log.id}`} hover selected={rowExpanded}>
          <TableCell>
            <Tooltip arrow title={rowExpanded ? t('f-com-hide') : t('f-com-expand')}>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setRowExpanded(!rowExpanded)}
                sx={V4_ICON_BUTTON_SMALL}
              >
                {rowExpanded ? <Icon>{'keyboard_arrow_up'}</Icon> : <Icon>{'keyboard_arrow_down'}</Icon>}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell>{renderActionTypeColumn(log.type)}</TableCell>
          <TableCell>{logTime.toLocaleDateString()}</TableCell>
          <TableCell>{logTime.toLocaleTimeString()}</TableCell>
          <TableCell>{log.number || '-'}</TableCell>
          <TableCell>{log.macAddress || '-'}</TableCell>
        </TableRow>

        <TableRow key={`row${log.id}`}>
          <TableCell sx={styles.tableRowArrowCell} colSpan={6}>
            <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
              {renderActionLogDetailsSection(log)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  const renderActionLogDetailsSection = (log: UserActionLog) => {
    if (!log.data) return '-';

    return (
      <Grid container>
        <Grid item lg={6} sm={12}>
          <Typography variant="h6">{t('f-act-details')}</Typography>
          <JSONTree data={JSON.parse(log.data)} theme={colors.jsonViewTheme} />
        </Grid>
      </Grid>
    );
  };

  const clearFilters = () => {
    setNumber('');
    setMacAddress('');
    setSelectedDateFrom(new Date());
    setSelectedDateTo(new Date());

    setActionType(0);
  };

  const search = () => {
    setPage(0);
    setLoaded(false);
  };

  return (
    <Paper sx={styles.paper} elevation={1}>
      <Grid container spacing={1}>
        <Grid container direction="row">
          <Grid container item spacing={1} xs={10}>
            <Grid item>
              <Icon color="primary" fontSize="large">
                {'history'}
              </Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-usr-log')}</Title>
            </Grid>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>
          <Grid item xs={2} sx={styles.loadInjectionsBtnGrid}>
            <ReloadIconButton action={() => loadUserLogs()} />
          </Grid>
        </Grid>

        <Grid item container md={12} spacing={1} sx={styles.centered}>
          <Grid item md={2} xs={10}>
            <ActionTypeSelect
              value={actionType}
              onChange={(type) => {
                setActionType(type);
              }}
              cssClass="!m-2"
              allowAny
              fullWidth
            />
          </Grid>

          <Grid item md={2} xs={10}>
            <TextField
              label={t('f-chip-number')}
              variant="outlined"
              size="small"
              fullWidth
              value={number}
              sx={styles.formControl}
              onChange={handleChipNumberChange}
            />
          </Grid>

          <Grid item md={2} xs={10}>
            <TextField
              label={t('f-com-mac-address')}
              variant="outlined"
              size="small"
              fullWidth
              value={macAddress}
              sx={styles.formControl}
              onChange={handleMacAddressChange}
            />
          </Grid>

          <Grid item sx={styles.withMarginLeft}>
            <ClearFilters onClick={clearFilters} />
          </Grid>

          <Grid item>
            <Button
              id="searchBtn"
              variant="contained"
              color="primary"
              size="small"
              sx={styles.formControl}
              onClick={search}
              startIcon={<Icon>{'search'}</Icon>}
            >
              {t('f-search')}
            </Button>
          </Grid>
        </Grid>

        <Grid item container md={12} spacing={1} sx={styles.centered}>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                format="dd/MM/yyyy"
                label={t('f-search-date-from')}
                value={selectedDateFrom}
                onChange={handleDateChangeFrom}
                slotProps={{
                  textField: {
                    margin: 'normal',
                    id: 'dateFromSelect',
                    'aria-label': 'change date'
                  }
                }}
              />
              <DesktopDatePicker
                format="dd/MM/yyyy"
                label={t('f-search-date-to')}
                value={selectedDateTo}
                onChange={handleDateChangeTo}
                slotProps={{
                  textField: {
                    margin: 'normal',
                    id: 'dateToSelect',
                    'aria-label': 'change date'
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item>
            <Button
              id="todayBtn"
              variant="contained"
              color="inherit"
              size="small"
              sx={{ ...V4_BUTTON_DEFAULT, ...styles.formControl }}
              onClick={setToday}
              startIcon={<Icon>{'event_available'}</Icon>}
            >
              {t('f-com-today')}
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && !isBusy && (
            <Paper>
              <Table size="small" sx={V4_TABLE}>
                <TableHead sx={styles.tableHeader}>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>{t('f-act-type')}</TableCell>
                    <TableCell>{t('f-com-date')}</TableCell>
                    <TableCell>{t('f-com-time')}</TableCell>
                    <TableCell>{t('f-chip-number')}</TableCell>
                    <TableCell>{t('f-com-mac-address')}</TableCell>
                  </TableRow>
                </TableHead>

                {actionLogs && actionLogs.length > 0 && (
                  <TableBody>
                    {actionLogs.map((log, index) => (
                      <ActionLogTableRow log={log} key={index} />
                    ))}
                  </TableBody>
                )}

                {actionLogs.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell variant="footer" align="right">
                        <Chip sx={{ ...V4_CHIP_DEFAULT, ...styles.tableInfoChip }} label={t('f-act-no-data')} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
});

export default UserActionLogView;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    formControl: {
      margin: theme.spacing(1)
    },
    loadInjectionsBtnGrid: {
      textAlign: 'right'
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    tableHeaderFooter: {
      background: colors.defaultGrey
    },
    tableRowArrowCell: {
      paddingBottom: 0,
      paddingTop: 0
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    centered: {
      alignItems: 'center'
    }
  }) as const;
